/**
 * WebAuthn helpers for unlocking a secret protected by security keys.
 *
 * Protocol (SharePass-Infra `pull-secret`, filter 6):
 * 1. `GET /secret/view` without `challengeresponse` answers `Success` with
 *    `details: { challenge, allowedCredentials: [{ id, transports }] }`.
 * 2. The browser signs that challenge with one of the allowed keys.
 * 3. `GET /secret/view` again with header `challengeresponse`: base64 of the JSON
 *    `{ clientDataJSON, authenticatorData, signature, credId }` (binary fields base64).
 *    The server verifies the signature with the key's stored public key and returns the secret.
 */

import { base64ToBytes, bytesToBase64 } from '@/utils/crypto/crypto';

export interface AllowedCredential {
    /** credential id, base64url */
    id: string;
    transports?: string[] | null;
}

export interface SecurityKeyChallenge {
    challenge: string;
    allowedCredentials: AllowedCredential[];
}

const KNOWN_TRANSPORTS: readonly AuthenticatorTransport[] = ['usb', 'nfc', 'ble', 'internal', 'hybrid'];

/** Two minutes to find and touch the key. */
export const ASSERTION_TIMEOUT_MS = 120_000;

/**
 * The relying-party id keys for secrets are registered under: `sharepass.com`, the parent of the
 * app and the reveal (SharePass-Infra SECRET_KEYS_RPID, Frontend-V2 VITE_WEBAUTHN_RP_ID). Override
 * with VITE_WEBAUTHN_RP_ID for another domain.
 */
export const getRpId = (): string => import.meta.env.VITE_WEBAUTHN_RP_ID?.trim() || 'sharepass.com';

const toBufferSource = (bytes: Uint8Array): Uint8Array<ArrayBuffer> => new Uint8Array(bytes);

/** Whether a `/secret/view` payload is a security-key challenge. */
export const isSecurityKeyChallenge = (details: unknown): details is SecurityKeyChallenge => {
    if (!details || typeof details !== 'object') return false;
    const candidate = details as Partial<SecurityKeyChallenge>;
    return (
        typeof candidate.challenge === 'string' &&
        candidate.challenge.length > 0 &&
        Array.isArray(candidate.allowedCredentials) &&
        candidate.allowedCredentials.length > 0
    );
};

/**
 * `navigator.credentials.get()` options for a server challenge. The challenge string is decoded
 * as base64 (what the previous reveal did), so both clients sign the same bytes.
 */
export const buildRequestOptions = (
    { challenge, allowedCredentials }: SecurityKeyChallenge,
    rpId: string = getRpId()
): PublicKeyCredentialRequestOptions => ({
    challenge: toBufferSource(base64ToBytes(challenge)),
    rpId,
    timeout: ASSERTION_TIMEOUT_MS,
    // matches how keys are registered (authenticatorUserVerification: "discouraged")
    userVerification: 'discouraged',
    allowCredentials: allowedCredentials.map((credential) => ({
        type: 'public-key' as const,
        id: toBufferSource(base64ToBytes(credential.id)),
        transports: (credential.transports ?? []).filter((t): t is AuthenticatorTransport =>
            KNOWN_TRANSPORTS.includes(t as AuthenticatorTransport)
        ),
    })),
});

const bufferToBase64 = (buffer: ArrayBuffer): string => bytesToBase64(new Uint8Array(buffer));

/** The `challengeresponse` header value for a signed assertion. */
export const encodeChallengeResponse = (credential: {
    id: string;
    response: Pick<AuthenticatorAssertionResponse, 'clientDataJSON' | 'authenticatorData' | 'signature'>;
}): string => {
    const payload = JSON.stringify({
        clientDataJSON: bufferToBase64(credential.response.clientDataJSON),
        authenticatorData: bufferToBase64(credential.response.authenticatorData),
        signature: bufferToBase64(credential.response.signature),
        credId: credential.id,
    });

    return bytesToBase64(new TextEncoder().encode(payload));
};

/** A message the recipient can act on, for the errors `navigator.credentials.get()` throws. */
export const describeWebAuthnError = (error: unknown): string => {
    const name = error instanceof DOMException || error instanceof Error ? error.name : '';

    switch (name) {
        case 'NotAllowedError':
            return 'The security key request was cancelled or timed out. Please try again.';
        case 'SecurityError':
            return 'This security key cannot be used on this page. Ask the sender to share the secret again.';
        case 'InvalidStateError':
            return 'This security key is not one of the keys allowed for this secret.';
        case 'NotSupportedError':
            return 'This browser does not support security keys.';
        default:
            return error instanceof Error && error.message ? error.message : 'Security key authentication failed.';
    }
};

/** Asks the browser for an assertion and returns the `challengeresponse` header value. */
export const signChallenge = async (challenge: SecurityKeyChallenge): Promise<string> => {
    if (typeof window === 'undefined' || !window.PublicKeyCredential || !navigator.credentials) {
        throw new Error('This browser does not support security keys.');
    }

    const credential = (await navigator.credentials.get({
        publicKey: buildRequestOptions(challenge),
    })) as PublicKeyCredential | null;

    if (!credential) {
        throw new Error('The security key request was cancelled.');
    }

    return encodeChallengeResponse({
        id: credential.id,
        response: credential.response as AuthenticatorAssertionResponse,
    });
};
